import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
/**
 * Horizontal-scroll slider behavior — Swiper-style gesture model wrapping a
 * native-scroll track. Owns:
 *
 *   - Velocity-tracked mouse drag with activation threshold (left-click drag
 *     below threshold falls through as a click so `<a>` children still navigate)
 *   - rAF-coalesced scrollLeft writes + `data-slider-dragging` attribute so
 *     consumers can turn off `scroll-snap-type` during the drag
 *   - `dragstart` preventDefault so dragging over images / anchors doesn't
 *     hand control to the browser's native link-drag engine
 *   - Momentum projection + JS ease-out-cubic settle to the nearest snap
 *     (slide offset or track edge), matching Swiper's feel
 *   - One-shot capture-phase click swallower so ending a drag mid-card doesn't
 *     accidentally fire the anchor click
 *   - prev/next `scrollBy(direction)` that steps by one slide + current gap
 *     and snaps to the track edge when the next step would leave under half
 *     a slide remaining (prevents the "one extra click" edge case)
 *
 * Touch is intentionally untouched — native `overflow-x: auto` on the track
 * already delivers OS-level momentum there, which any JS replacement would
 * regress.
 *
 * Usage:
 *
 *   const { trackRef, scrollProgress, canScrollPrev, canScrollNext, scrollBy } =
 *     useSlider();
 *
 *   <div ref={trackRef} className="track">…slides…</div>
 *
 * The track must be a flex container whose direct children are the slides.
 * The hook reads `slide.offsetWidth` + track `gap` (via getComputedStyle) to
 * compute the step per prev/next click, so no selectors or class names need
 * to be passed in.
 */
export function useSlider(options = {}) {
    const { activateThresholdPx = 5, shortSwipeMaxMs = 300, edgeTolerancePx = 4, settleMinMs = 260, settleMaxMs = 500, } = options;
    const DRAGGING_ATTR = 'data-slider-dragging';
    const [trackEl, setTrackEl] = useState(null);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    // Imperative drag/animation bookkeeping — kept in a ref so mousemove /
    // settle callbacks don't retain stale closures across React renders.
    const stateRef = useRef({
        pressed: false,
        captured: false,
        startX: 0,
        startScroll: 0,
        startTime: 0,
        lastX: 0,
        moved: 0,
        rafId: 0,
        animId: 0,
    });
    const cancelSettle = useCallback(() => {
        const s = stateRef.current;
        if (s.animId) {
            cancelAnimationFrame(s.animId);
            s.animId = 0;
        }
    }, []);
    const measureStep = useCallback((el) => {
        const firstChild = el.firstElementChild;
        if (!firstChild)
            return Math.max(0, el.clientWidth * 0.8);
        const gap = parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap || '0') || 0;
        return firstChild.offsetWidth + gap;
    }, []);
    /**
     * Snap position nearest to `target` in scroll-distance. Candidates are every
     * slide's `offsetLeft` plus the two track edges (0, maxScroll). Used when a
     * drag covered enough distance that the user's release *position* should win
     * over a fixed one-slide-step rule.
     */
    const findNearestSnap = useCallback((el, target) => {
        const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
        const candidates = [0, maxScroll];
        Array.from(el.children).forEach(child => {
            const offset = child.offsetLeft;
            if (offset <= maxScroll)
                candidates.push(offset);
        });
        let best = candidates[0];
        for (const c of candidates) {
            if (Math.abs(c - target) < Math.abs(best - target))
                best = c;
        }
        return Math.max(0, Math.min(maxScroll, best));
    }, []);
    /**
     * Snap position immediately to the left/right of `from`. Used for short-swipe
     * flicks so a quick small-distance gesture still advances by exactly one
     * slide even if the user barely moved their mouse.
     */
    const findAdjacentSnap = useCallback((el, from, direction) => {
        const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
        const slides = Array.from(el.children);
        if (direction === 0)
            return Math.max(0, Math.min(maxScroll, from));
        if (direction > 0) {
            // Smallest slide offset strictly greater than `from`.
            let target = maxScroll;
            for (const slide of slides) {
                if (slide.offsetLeft > from + 1 && slide.offsetLeft < target) {
                    target = slide.offsetLeft;
                }
            }
            return Math.min(maxScroll, target);
        }
        // Largest slide offset strictly less than `from`.
        let target = 0;
        for (const slide of slides) {
            if (slide.offsetLeft < from - 1 && slide.offsetLeft > target) {
                target = slide.offsetLeft;
            }
        }
        return Math.max(0, target);
    }, []);
    const animateScrollTo = useCallback((el, to) => {
        cancelSettle();
        const from = el.scrollLeft;
        const delta = to - from;
        if (Math.abs(delta) < 0.5) {
            el.scrollLeft = to;
            return;
        }
        const duration = Math.min(settleMaxMs, Math.max(settleMinMs, Math.abs(delta) * 0.5));
        const t0 = performance.now();
        const s = stateRef.current;
        const step = () => {
            const t = Math.min(1, (performance.now() - t0) / duration);
            const eased = 1 - Math.pow(1 - t, 3); // ease-out-cubic
            el.scrollLeft = from + delta * eased;
            if (t < 1)
                s.animId = requestAnimationFrame(step);
            else
                s.animId = 0;
        };
        s.animId = requestAnimationFrame(step);
    }, [cancelSettle, settleMaxMs, settleMinMs]);
    const scrollBy = useCallback((direction) => {
        const el = trackEl;
        if (!el)
            return;
        cancelSettle();
        const step = measureStep(el);
        const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
        let target = el.scrollLeft + direction * step;
        // If the next step would leave less than half a slide of room, jump to
        // the edge so the final click fully closes the gap (otherwise snap lands
        // 1–2 px short and we'd need a second click to flip canScrollNext).
        if (direction > 0 && maxScroll - target < step / 2)
            target = maxScroll;
        if (direction < 0 && target < step / 2)
            target = 0;
        target = Math.max(0, Math.min(maxScroll, target));
        animateScrollTo(el, target);
    }, [trackEl, cancelSettle, measureStep, animateScrollTo]);
    // Scroll state + resize observation.
    useEffect(() => {
        if (!trackEl)
            return;
        const el = trackEl;
        const updateScrollState = () => {
            const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
            setCanScrollPrev(el.scrollLeft > edgeTolerancePx);
            setCanScrollNext(el.scrollLeft < maxScroll - edgeTolerancePx);
            setScrollProgress(maxScroll > 0 ? Math.min(1, Math.max(0, el.scrollLeft / maxScroll)) : 0);
        };
        updateScrollState();
        el.addEventListener('scroll', updateScrollState, { passive: true });
        window.addEventListener('resize', updateScrollState);
        // Track content (children) changes too — recs load asynchronously, and
        // skeleton → product swaps would otherwise leave the prev/next/progress
        // state stuck at the skeleton measurements.
        const mo = new MutationObserver(updateScrollState);
        mo.observe(el, { childList: true, subtree: false });
        return () => {
            el.removeEventListener('scroll', updateScrollState);
            window.removeEventListener('resize', updateScrollState);
            mo.disconnect();
        };
    }, [trackEl, edgeTolerancePx]);
    // Drag handling (left-click only; touch uses native pan).
    useEffect(() => {
        if (!trackEl)
            return;
        const el = trackEl;
        const s = stateRef.current;
        const onMouseMove = (event) => {
            if (!s.pressed)
                return;
            s.lastX = event.clientX;
            const dx = s.lastX - s.startX;
            s.moved = Math.max(s.moved, Math.abs(dx));
            if (!s.captured && Math.abs(dx) > activateThresholdPx) {
                s.captured = true;
                el.setAttribute(DRAGGING_ATTR, 'true');
                document.body.style.userSelect = 'none';
            }
            if (!s.captured)
                return;
            event.preventDefault();
            if (!s.rafId) {
                s.rafId = requestAnimationFrame(() => {
                    s.rafId = 0;
                    if (!s.pressed)
                        return;
                    el.scrollLeft = s.startScroll - (s.lastX - s.startX);
                });
            }
        };
        const onMouseUp = () => {
            if (!s.pressed)
                return;
            s.pressed = false;
            if (s.rafId) {
                cancelAnimationFrame(s.rafId);
                s.rafId = 0;
            }
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            if (!s.captured)
                return; // pure click, let it through
            el.removeAttribute(DRAGGING_ATTR);
            document.body.style.userSelect = '';
            // One-shot capture-phase click swallower so releasing mid-card after a
            // drag doesn't navigate to the anchor under the cursor.
            const suppressClick = (ce) => {
                ce.preventDefault();
                ce.stopPropagation();
            };
            document.addEventListener('click', suppressClick, { capture: true, once: true });
            setTimeout(() => document.removeEventListener('click', suppressClick, { capture: true }), 100);
            // Hybrid release logic, tuned for thumbnail/product carousels:
            //
            //   1. Trivial drag (< activation threshold): snap back to start — the
            //      user basically didn't move.
            //   2. Quick flick covering less than one slide: apply Swiper's "short
            //      swipe" rule and advance by exactly one slide in the drag
            //      direction. Intent was clearly "next/prev", distance doesn't
            //      matter.
            //   3. Everything else: snap to the slide boundary nearest the current
            //      release position. This lets slow drags that covered 2-3 slides
            //      actually *land* on slide 2 or 3 — matching what the user saw
            //      under their cursor during the drag — rather than yanking them
            //      back to a 1-slide-advance that's already 400 px behind the
            //      cursor (the jarring "disconnect" feeling).
            //
            // Long drags past `longSwipeRatio` of a slide are handled implicitly
            // by case 3: the release position will sit past the midpoint of the
            // adjacent slide, so findNearestSnap returns that next slide.
            const duration = performance.now() - s.startTime;
            const dragDx = el.scrollLeft - s.startScroll;
            const slideSize = measureStep(el) || el.clientWidth;
            const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
            let target;
            if (Math.abs(dragDx) < activateThresholdPx) {
                target = Math.max(0, Math.min(maxScroll, s.startScroll));
            }
            else if (duration < shortSwipeMaxMs && Math.abs(dragDx) < slideSize) {
                // Quick short flick — commit to an adjacent slide in drag direction
                // even if the raw distance wouldn't have reached it.
                target = findAdjacentSnap(el, s.startScroll, dragDx > 0 ? 1 : -1);
            }
            else {
                // Longer/larger drag — land where the user actually dragged,
                // snapped to the nearest slide boundary. Naturally handles slow
                // multi-slide drags and long-distance flicks.
                target = findNearestSnap(el, el.scrollLeft);
            }
            // Edge-snap tolerance: if the target leaves less than half a slide of
            // room to the track edge, jump to the edge so disabled-state flips on
            // the same release (avoids the "one extra click" bug).
            if (maxScroll - target > 0 && maxScroll - target < slideSize / 2)
                target = maxScroll;
            if (target > 0 && target < slideSize / 2)
                target = 0;
            animateScrollTo(el, target);
        };
        const onMouseDown = (event) => {
            if (event.button !== 0)
                return;
            cancelSettle();
            s.pressed = true;
            s.captured = false;
            s.startX = event.clientX;
            s.startScroll = el.scrollLeft;
            s.startTime = performance.now();
            s.lastX = event.clientX;
            s.moved = 0;
            document.addEventListener('mousemove', onMouseMove, { passive: false });
            document.addEventListener('mouseup', onMouseUp, { passive: false });
        };
        // Block native drag from images / anchors so pointer events keep flowing
        // to us when dragging over a product card.
        const onDragStart = (event) => event.preventDefault();
        el.addEventListener('mousedown', onMouseDown);
        el.addEventListener('dragstart', onDragStart);
        return () => {
            el.removeEventListener('mousedown', onMouseDown);
            el.removeEventListener('dragstart', onDragStart);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            if (s.rafId)
                cancelAnimationFrame(s.rafId);
            cancelSettle();
            el.removeAttribute(DRAGGING_ATTR);
        };
    }, [
        trackEl,
        activateThresholdPx,
        shortSwipeMaxMs,
        cancelSettle,
        measureStep,
        findAdjacentSnap,
        findNearestSnap,
        animateScrollTo,
    ]);
    return {
        trackRef: setTrackEl,
        scrollProgress,
        canScrollPrev,
        canScrollNext,
        scrollBy,
    };
}
